import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  CloudSyncOutlined,
  ExperimentOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { ApiService } from "../../api";
import type {
  AdminGroup,
  DirectoryGroupLink,
  DirectorySyncRun,
  IntegrationStatus,
  SyncReport,
} from "../../api";
import { apiErrorMessage } from "../../api/apiError";
import { Panel, SectionLabel, Stat } from "../../components/surfaces";
import { space } from "../../theme/space";

const { Text, Paragraph } = Typography;

type Props = {
  status: IntegrationStatus | null;
  groups: AdminGroup[];
  onChanged: () => void;
};

/**
 * Directory synchronisation and Teams, on one screen.
 *
 * Together because they share one Entra application: reporting them apart lets
 * somebody fix the Teams credentials and wonder why the directory still fails.
 *
 * **Rehearse before you run.** The first sync into a live deployment decides
 * whether four hundred people keep their accounts, so the primary action here
 * is the dry run, and the real one sits beside it. The report is the same
 * either way — the only difference is whether anything was written.
 *
 * **Nothing runs itself.** There is no scheduler in this product, and the panel
 * says so rather than implying a nightly job that does not exist. The command
 * to put in cron is printed where somebody can copy it.
 */
const DirectoryPanel = ({ status, groups, onChanged }: Props) => {
  const { t } = useTranslation();
  const [runs, setRuns] = useState<DirectorySyncRun[]>([]);
  const [links, setLinks] = useState<DirectoryGroupLink[]>([]);
  const [report, setReport] = useState<SyncReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkGroup, setLinkGroup] = useState<number | undefined>();
  const [linkExternalId, setLinkExternalId] = useState("");

  const load = useCallback(async () => {
    try {
      const [runRows, linkRows] = await Promise.all([
        ApiService.apiAccountsSyncRunsList(),
        ApiService.apiAccountsGroupLinksList(),
      ]);
      setRuns(runRows);
      setLinks(linkRows);
    } catch (e) {
      message.error(apiErrorMessage(e, t("Could not load the sync history")));
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const directory = status?.directory;
  const teams = status?.teams;
  const configured = Boolean(directory?.enabled);

  const test = async () => {
    setBusy(true);
    try {
      const result = await ApiService.apiAccountsDirectoryTest();
      if (result.ok) message.success(result.detail);
      else message.error(result.detail);
    } catch (e) {
      message.error(apiErrorMessage(e, t("Could not reach the directory")));
    }
    setBusy(false);
  };

  const run = async (dryRun: boolean) => {
    setBusy(true);
    setReport(null);
    try {
      const result = await ApiService.apiAccountsDirectorySync({ dry_run: dryRun });
      setReport(result);
      if (result.error) message.error(result.error);
      else if (dryRun) message.success(t("Rehearsal finished — nothing was written."));
      else message.success(t("Synchronised."));
      load();
      onChanged();
    } catch (e) {
      message.error(apiErrorMessage(e, t("The sync could not be started")));
    }
    setBusy(false);
  };

  const confirmRealRun = () =>
    Modal.confirm({
      title: t("Synchronise for real?"),
      content: t(
        "People, units and linked group membership will be written. Nobody is ever deleted — someone who has left the directory is switched off, and only if that has been enabled.",
      ),
      okText: t("Synchronise"),
      cancelText: t("Cancel"),
      onOk: () => run(false),
    });

  const addLink = async () => {
    if (!linkGroup || !linkExternalId.trim()) return;
    try {
      await ApiService.apiAccountsGroupLinksCreate({
        group: linkGroup,
        external_id: linkExternalId.trim(),
        source: directory?.provider === "ldap" ? "Ldap" : "Entra",
      });
      setLinking(false);
      setLinkGroup(undefined);
      setLinkExternalId("");
      load();
      onChanged();
    } catch (e) {
      message.error(apiErrorMessage(e, t("Could not link the group")));
    }
  };

  const removeLink = async (link: DirectoryGroupLink) => {
    try {
      await ApiService.apiAccountsGroupLinksDelete(link.id);
      load();
      onChanged();
    } catch (e) {
      message.error(apiErrorMessage(e, t("Could not unlink the group")));
    }
  };

  return (
    <Space direction="vertical" size={space.wide} style={{ width: "100%" }}>
      {!configured && (
        <Alert
          type="info"
          showIcon
          message={t("No company directory is connected")}
          description={t(
            "Set DIRECTORY_PROVIDER to 'entra' or 'ldap' in the deployment, with its credentials, and this screen can keep people, their departments and their reporting lines up to date automatically. Until then everything is maintained by hand on the People and Structure tabs, which works perfectly well.",
          )}
        />
      )}

      <Panel>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: space.wide,
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 240, flex: 1 }}>
            <SectionLabel>{t("Connection")}</SectionLabel>
            <div style={{ marginTop: space.tight }}>
              <Space direction="vertical" size={4}>
                {directory?.providers.map((p) => (
                  <Space key={p.value} size={6}>
                    <Text strong={directory.provider === p.value}>{p.label}</Text>
                    {directory.provider === p.value && (
                      <Tag color="blue">{t("In use")}</Tag>
                    )}
                    {!p.available && (
                      <Tooltip
                        title={t(
                          "The package that speaks this protocol is not installed in this deployment.",
                        )}
                      >
                        <Tag>{t("not installed")}</Tag>
                      </Tooltip>
                    )}
                    {p.available &&
                      (p.configured ? (
                        <Tag color="green">{t("credentials set")}</Tag>
                      ) : (
                        <Tag>{t("no credentials")}</Tag>
                      ))}
                  </Space>
                ))}
              </Space>
            </div>
          </div>

          <div style={{ minWidth: 240, flex: 1 }}>
            <SectionLabel>{t("What a sync will do")}</SectionLabel>
            <div style={{ marginTop: space.tight }}>
              <Space direction="vertical" size={2}>
                <Text style={{ fontSize: 13 }}>
                  {directory?.create_units
                    ? t("Departments become organisation units.")
                    : t("Departments are not turned into units.")}
                </Text>
                <Text style={{ fontSize: 13 }}>
                  {t("{{count}} group(s) are linked and will have their membership replaced.", {
                    count: status?.linked_groups ?? 0,
                  })}
                </Text>
                <Text
                  style={{ fontSize: 13 }}
                  type={directory?.deactivate_missing ? "warning" : undefined}
                >
                  {directory?.deactivate_missing
                    ? t(
                        "People missing from the directory will be switched off — never hand-made accounts or administrators.",
                      )
                    : t("Nobody is switched off for being missing from the directory.")}
                </Text>
              </Space>
            </div>
          </div>

          <Space direction="vertical" size={space.tight}>
            <Button
              icon={<CheckCircleOutlined />}
              disabled={!configured || busy}
              onClick={test}
            >
              {t("Test the connection")}
            </Button>
            <Button
              type="primary"
              icon={<ExperimentOutlined />}
              disabled={!configured}
              loading={busy}
              onClick={() => run(true)}
            >
              {t("Rehearse a sync")}
            </Button>
            <Button
              icon={<CloudSyncOutlined />}
              disabled={!configured || busy}
              onClick={confirmRealRun}
            >
              {t("Synchronise now")}
            </Button>
          </Space>
        </div>

        <Paragraph
          type="secondary"
          style={{ fontSize: 12, marginTop: space.base, marginBottom: 0 }}
        >
          {t(
            "Nothing here runs on its own — this product has no scheduler. To keep the directory fresh, run this nightly from the host:",
          )}{" "}
          <Text code>manage.py sync_directory</Text>
        </Paragraph>
      </Panel>

      {report && (
        <Panel>
          <SectionLabel>
            {report.dry_run ? t("Rehearsal — nothing was written") : t("Last run")}
          </SectionLabel>
          <div
            style={{
              display: "flex",
              gap: space.band,
              flexWrap: "wrap",
              marginTop: space.snug,
            }}
          >
            <Stat label={t("Seen")} value={report.users_seen} />
            <Stat label={t("Added")} value={report.users_created} />
            <Stat label={t("Updated")} value={report.users_updated} />
            <Stat label={t("Switched off")} value={report.users_deactivated} />
            <Stat label={t("Units added")} value={report.units_created} />
            <Stat label={t("Reporting lines")} value={report.managers_linked} />
          </div>
          {report.error && (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: space.base }}
              message={t("The sync failed and nothing was changed")}
              description={report.error}
            />
          )}
          {report.warnings?.length > 0 && (
            <Alert
              type="warning"
              style={{ marginTop: space.base }}
              message={t("Worth knowing")}
              description={
                <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                  {report.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              }
            />
          )}
          {report.details?.length > 0 && (
            <Table
              size="small"
              style={{ marginTop: space.base }}
              rowKey={(r) => `${r.action}-${r.subject}`}
              dataSource={report.details}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              columns={[
                { title: t("What"), dataIndex: "action", width: 160 },
                { title: t("Who or what"), dataIndex: "subject" },
                { title: t("Detail"), dataIndex: "detail" },
              ]}
            />
          )}
        </Panel>
      )}

      <Panel>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: space.snug,
            marginBottom: space.snug,
          }}
        >
          <SectionLabel style={{ flex: 1 }}>
            {t("Groups the directory may manage")}
          </SectionLabel>
          <Button
            size="small"
            icon={<LinkOutlined />}
            disabled={!configured}
            onClick={() => setLinking(true)}
          >
            {t("Link a group")}
          </Button>
        </div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t(
            "Only linked groups are touched. Everything else — every approval pool somebody put together by hand — is left exactly as it is, because a sync that guessed by name would empty one the first time the directory happened to have a group of the same name.",
          )}
        </Text>
        <Table
          size="small"
          style={{ marginTop: space.snug }}
          rowKey="id"
          dataSource={links}
          pagination={false}
          locale={{ emptyText: t("No groups are linked, so the sync touches none.") }}
          columns={[
            { title: t("Group here"), dataIndex: "group_name" },
            {
              title: t("In the directory"),
              dataIndex: "external_name",
              render: (name: string, row: DirectoryGroupLink) =>
                name || <Text code>{row.external_id}</Text>,
            },
            {
              title: t("Last synced"),
              dataIndex: "synced_at",
              render: (value: string | null) =>
                value ? new Date(value).toLocaleString() : <Text type="secondary">—</Text>,
            },
            {
              title: "",
              key: "actions",
              width: 100,
              render: (_: unknown, row: DirectoryGroupLink) => (
                <Button size="small" danger onClick={() => removeLink(row)}>
                  {t("Unlink")}
                </Button>
              ),
            },
          ]}
        />
      </Panel>

      <Panel>
        <SectionLabel>{t("Microsoft Teams")}</SectionLabel>
        <div style={{ marginTop: space.tight }}>
          {teams?.enabled ? (
            <Space direction="vertical" size={2}>
              <Text style={{ fontSize: 13 }}>
                {teams.webhook_configured
                  ? t(
                      "New and fully approved cases are announced in the connected channel.",
                    )
                  : t("No channel webhook, so nothing is announced to a channel.")}
              </Text>
              <Text style={{ fontSize: 13 }}>
                {teams.dm_enabled
                  ? t(
                      "Approvals, rework and assignments are also sent to the person as a direct message — everyone who has been through a directory sync.",
                    )
                  : t(
                      "Direct messages are off, so approvals arrive by email only.",
                    )}
              </Text>
            </Space>
          ) : (
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t(
                "Teams notifications are off. Turn them on with TEAMS_NOTIFICATIONS_ENABLED and give the deployment a channel webhook, a direct-message app, or both. Email is unaffected either way — Teams is a second channel, never a replacement.",
              )}
            </Text>
          )}
        </div>
      </Panel>

      <Panel>
        <SectionLabel>{t("Past runs")}</SectionLabel>
        <Table
          size="small"
          style={{ marginTop: space.snug }}
          rowKey="id"
          dataSource={runs}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          locale={{ emptyText: t("Nothing has been synchronised yet.") }}
          columns={[
            {
              title: t("When"),
              dataIndex: "started_at",
              render: (value: string) => new Date(value).toLocaleString(),
            },
            { title: t("Directory"), dataIndex: "provider", width: 100 },
            {
              title: t("Outcome"),
              dataIndex: "outcome",
              width: 150,
              render: (outcome: string, row: DirectorySyncRun) => (
                <Space size={4}>
                  <Tag color={outcome === "Succeeded" ? "green" : "red"}>
                    {outcome === "Succeeded" ? t("Succeeded") : t("Failed")}
                  </Tag>
                  {row.dry_run && <Tag>{t("rehearsal")}</Tag>}
                </Space>
              ),
            },
            { title: t("Seen"), dataIndex: "users_seen", width: 80 },
            { title: t("Added"), dataIndex: "users_created", width: 80 },
            { title: t("Updated"), dataIndex: "users_updated", width: 90 },
            { title: t("Switched off"), dataIndex: "users_deactivated", width: 110 },
            {
              title: t("Started by"),
              dataIndex: "started_by_name",
              render: (value: string) =>
                value || <Text type="secondary">{t("a scheduled run")}</Text>,
            },
          ]}
        />
      </Panel>

      <Modal
        open={linking}
        title={t("Link a group to the directory")}
        onCancel={() => setLinking(false)}
        onOk={addLink}
        okText={t("Link it")}
        cancelText={t("Cancel")}
      >
        <Space direction="vertical" size={space.snug} style={{ width: "100%" }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t(
              "From the next sync onwards this group's membership comes from the directory, replacing whatever is in it now.",
            )}
          </Text>
          <Select
            showSearch
            optionFilterProp="label"
            style={{ width: "100%" }}
            placeholder={t("Which group here?")}
            value={linkGroup}
            onChange={setLinkGroup}
            options={groups
              .filter((g) => !g.directory_linked)
              .map((g) => ({ value: g.id, label: g.name }))}
          />
          <Select
            mode="tags"
            maxTagCount={1}
            style={{ width: "100%" }}
            placeholder={
              directory?.provider === "ldap"
                ? t("The group's distinguished name")
                : t("The group's object id in Entra")
            }
            value={linkExternalId ? [linkExternalId] : []}
            onChange={(v) => setLinkExternalId(v[v.length - 1] ?? "")}
            options={[]}
          />
        </Space>
      </Modal>
    </Space>
  );
};

export default DirectoryPanel;
